<template>
    <v-container fluid class="admin-home">
        <button class="admin-home-exit" @click="goToApp">
            <v-icon size="16">mdi-arrow-left</v-icon>
            Volver a la app
        </button>

        <div v-for="section in sections" :key="section.name" class="admin-section">
            <div class="admin-section-title">{{ section.name }}</div>
            <div class="admin-grid">
                <div v-for="item in section.items" :key="item.route" class="admin-tile" @click="goTo(item.route)">
                    <div class="admin-tile-icon" :class="item.danger ? 'admin-tile-icon--danger' : null">
                        <v-icon size="20" :color="item.danger ? 'error' : 'accent'">{{ item.icon }}</v-icon>
                    </div>
                    <div class="admin-tile-body">
                        <div class="admin-tile-name">{{ item.name }}</div>
                        <div class="admin-tile-desc">{{ item.description }}</div>
                    </div>
                    <v-icon size="18" color="txtMuted">mdi-chevron-right</v-icon>
                </div>
            </div>
        </div>
    </v-container>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { adminRouteGuard } from '@/utils/Admin'

export default {
    name: 'AdminHome',
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            sections: [
                {
                    name: 'Cuentas',
                    items: [
                        {
                            name: 'Ficha de usuario',
                            description: 'Busca una cuenta y mira plan, Stripe, vínculos y contadores.',
                            icon: 'mdi-card-account-details-outline',
                            route: 'admin.user-detail',
                        },
                        {
                            name: 'Confirmar email',
                            description: 'Marca el email de un usuario como verificado en Auth.',
                            icon: 'mdi-email-check-outline',
                            route: 'admin.confirm-email',
                        },
                        {
                            name: 'Enlazar usuarios',
                            description: 'Vincula un alumno a un entrenador o un entrenador a una empresa.',
                            icon: 'mdi-account-multiple-plus-outline',
                            route: 'admin.attach-users',
                        },
                        {
                            name: 'Accesos',
                            description: 'Acceso total, plan forzado o tope de alumnos de una cuenta.',
                            icon: 'mdi-key-outline',
                            route: 'admin.user-access',
                        },
                        {
                            name: 'Invitaciones',
                            description: 'Invitaciones pendientes de alumnos y entrenadores.',
                            icon: 'mdi-email-fast-outline',
                            route: 'admin.invitations',
                        },
                        {
                            name: 'Empresa a entrenador',
                            description: 'Convierte una empresa en entrenador y suelta a sus entrenadores.',
                            icon: 'mdi-office-building-remove-outline',
                            route: 'admin.company-to-coach',
                            danger: true,
                        },
                        {
                            name: 'Restaurar cuenta',
                            description: 'Revive una cuenta eliminada que sigue en los 30 días de gracia.',
                            icon: 'mdi-account-reactivate-outline',
                            route: 'admin.restore-user',
                        },
                        {
                            name: 'Borrar usuario',
                            description: 'Elimina la cuenta por email (Auth + users) y libera el email.',
                            icon: 'mdi-account-remove-outline',
                            route: 'admin.delete-user',
                            danger: true,
                        },
                    ],
                },
                {
                    name: 'Contenido',
                    items: [
                        {
                            name: 'Subir ejercicios',
                            description: 'Carga masiva de ejercicios desde un archivo Excel.',
                            icon: 'mdi-file-upload-outline',
                            route: 'admin.excel',
                        },
                        {
                            name: 'Encuestas',
                            description: 'Crea o edita la encuesta activa y mira sus resultados.',
                            icon: 'mdi-poll',
                            route: 'admin.surveys',
                        },
                    ],
                },
                {
                    name: 'Sistema',
                    items: [
                        {
                            name: 'Errores',
                            description: 'Errores reportados por la app, agrupados por error y día.',
                            icon: 'mdi-bug-outline',
                            route: 'admin.errors',
                        },
                        {
                            name: 'Configuración global',
                            description: 'Flags de config/builder y versiones mínimas de las apps.',
                            icon: 'mdi-tune',
                            route: 'admin.config',
                        },
                        {
                            name: 'Tutoriales',
                            description: 'Alta, baja y edición de los videos de config/tutorials.',
                            icon: 'mdi-play-circle-outline',
                            route: 'admin.tutorials',
                        },
                    ],
                },
            ],
        }
    },
    computed: {
        ...mapState(['toolbarSubtitle']),
        homeRoute(vm = this) {
            switch (vm.account?.accountType) {
                case 'student':
                    return 'student.home'
                case 'company':
                    return 'company.home'
                case 'coach':
                case 'coach_company':
                    return 'coach.home'
                default:
                    return 'splash'
            }
        },
    },
    mounted() {
        this.setToolbarSubtitle(this.currentUser?.email || '')
    },
    destroyed() {
        const vm = this
        if (vm.toolbarSubtitle === vm.currentUser?.email) vm.setToolbarSubtitle('')
    },
    methods: {
        ...mapMutations(['setToolbarSubtitle']),
        goTo(route) {
            this.pushRoute({ name: route })
        },
        goToApp() {
            this.pushRoute({ name: this.homeRoute })
        },
    },
}
</script>

<style scoped>
.admin-home {
    max-width: 980px;
    padding: 16px 20px 40px;
}

.admin-home-exit {
    margin-bottom: 18px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid var(--v-outline2-base);
    background: var(--v-surface-base);
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 600;
}

.admin-home-exit:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.admin-section + .admin-section {
    margin-top: 26px;
}

.admin-section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
}

.admin-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
}

.admin-tile {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border-radius: 14px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
    cursor: pointer;
    transition: border-color 0.12s, transform 0.12s;
}

.admin-tile:hover {
    border-color: var(--v-accent-base);
    transform: translateY(-1px);
}

.admin-tile-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(51, 153, 153, 0.12);
    border: 1px solid rgba(51, 153, 153, 0.28);
}

.admin-tile-icon--danger {
    background: rgba(232, 92, 92, 0.1);
    border-color: rgba(232, 92, 92, 0.3);
}

.admin-tile-body {
    flex: 1;
    min-width: 0;
}

.admin-tile-name {
    font-size: 14px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.admin-tile-desc {
    font-size: 12px;
    line-height: 1.45;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}
</style>
